import type { EventDurationParts, EventsHubCard, EventsHubImageFocalPoint, LiveProgressParts } from '../types/events-hub'

const asRecord = (value: unknown): Record<string, unknown> | undefined =>
  typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : undefined

const asString = (value: unknown): string => (typeof value === 'string' ? value.trim() : '')

const asNumber = (value: unknown): number | undefined => (typeof value === 'number' && Number.isFinite(value) ? value : undefined)

const MS_PER_DAY = 24 * 60 * 60 * 1000

const formatDay = (value: Date, timeZone?: string): string =>
  new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    ...(timeZone ? { timeZone } : {}),
  }).format(value)

const calendarDayKey = (value: Date, timeZone?: string): string =>
  new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    ...(timeZone ? { timeZone } : {}),
  }).format(value)

const toUtcCalendarDay = (value: Date, timeZone?: string): number | undefined => {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      ...(timeZone ? { timeZone } : {}),
    }).formatToParts(value)
    const year = Number(parts.find(part => part.type === 'year')?.value)
    const month = Number(parts.find(part => part.type === 'month')?.value)
    const day = Number(parts.find(part => part.type === 'day')?.value)
    if (!year || !month || !day) return undefined
    return Date.UTC(year, month - 1, day)
  } catch {
    return undefined
  }
}

const inclusiveCalendarDays = (start: Date, end: Date, timeZone?: string): number | undefined => {
  const startDay = toUtcCalendarDay(start, timeZone)
  const endDay = toUtcCalendarDay(end, timeZone)
  if (startDay === undefined || endDay === undefined) return undefined
  return Math.round((endDay - startDay) / MS_PER_DAY) + 1
}

export const isOnlineEventLocation = (location: Record<string, unknown> | undefined): boolean => {
  const type = asString(location?.type).toLowerCase()
  return type === 'online' || type === 'virtual'
}

export const resolveEventDisplayTimeZone = (location: Record<string, unknown> | undefined, eventTimeZone?: string): string | undefined =>
  isOnlineEventLocation(location) ? undefined : eventTimeZone

export const formatEventDateLabel = (startIso: string, endIso: string, timeZone?: string): string => {
  const start = new Date(startIso)
  if (Number.isNaN(start.getTime())) return ''
  const end = new Date(endIso)
  if (Number.isNaN(end.getTime()) || calendarDayKey(start, timeZone) === calendarDayKey(end, timeZone)) {
    return formatDay(start, timeZone)
  }

  const startLabel = formatDay(start, timeZone)
  const endLabel = formatDay(end, timeZone)
  const startMonthYear = startLabel.replace(/\s+\d+,/, '')
  const endMonthYear = endLabel.replace(/\s+\d+,/, '')
  if (startMonthYear === endMonthYear) {
    const startDay = startLabel.replace(/^[A-Za-z]+\s+/, '').replace(/,.*/, '')
    const endDay = endLabel.replace(/^[A-Za-z]+\s+/, '').replace(/,.*/, '')
    const month = startLabel.replace(/\s+\d+,.*/, '')
    const year = startLabel.replace(/.*,\s*/, '')
    return `${month} ${startDay}–${endDay}, ${year}`
  }
  return `${startLabel} – ${endLabel}`
}

export const toEventLocationLabel = (location: Record<string, unknown> | undefined): string => {
  if (!location) return ''
  if (isOnlineEventLocation(location)) return 'Online'

  const city = asString(location.city)
  const region = asString(location.state) || asString(location.country)
  if (city && region && region !== city) return `${city}, ${region}`
  return city || asString(location.venue) || asString(location.address)
}

export const toEventLocationParts = (location: Record<string, unknown> | undefined): { venue: string; address: string } => {
  if (!location) return { venue: '', address: '' }
  if (isOnlineEventLocation(location)) return { venue: 'Online', address: '' }

  const venue = asString(location.venue)
  const street = asString(location.address)
  const city = asString(location.city)
  const region = [asString(location.state), asString(location.zip)].filter(Boolean).join(' ')
  const cityLine = [city, region].filter(Boolean).join(', ')
  const primary = venue || city || street
  const secondary = venue ? cityLine || street : city ? region || street : ''

  return {
    venue: primary,
    address: secondary && secondary !== primary ? secondary : '',
  }
}

export const formatEventTimeLabel = (startIso: string, timeZone?: string, displayFormat: '12h' | '24h' = '12h'): string => {
  const date = new Date(startIso)
  if (Number.isNaN(date.getTime())) return ''

  const options: Intl.DateTimeFormatOptions = {
    hour: displayFormat === '24h' ? '2-digit' : 'numeric',
    minute: '2-digit',
    hour12: displayFormat !== '24h',
    timeZoneName: 'short',
    ...(timeZone ? { timeZone } : {}),
  }

  try {
    return new Intl.DateTimeFormat('en-US', options).format(date)
  } catch {
    if (!timeZone) return ''
    return formatEventTimeLabel(startIso, undefined, displayFormat)
  }
}

export const formatEventWeekdayTimeLabel = (startIso: string, timeZone?: string, displayFormat: '12h' | '24h' = '12h'): string => {
  const date = new Date(startIso)
  if (Number.isNaN(date.getTime())) return ''
  const time = formatEventTimeLabel(startIso, timeZone, displayFormat)
  if (!time) return ''

  try {
    const weekday = new Intl.DateTimeFormat('en-US', {
      weekday: 'short',
      ...(timeZone ? { timeZone } : {}),
    }).format(date)
    return `${weekday}, ${time}`
  } catch {
    if (!timeZone) return ''
    return formatEventWeekdayTimeLabel(startIso, undefined, displayFormat)
  }
}

export const formatEventDateChip = (startIso: string, endIso: string, timeZone?: string): string =>
  formatEventDateLabel(startIso, endIso, timeZone)
    .replace(/,\s*\d{4}/g, '')
    .trim()

export const toEventListingFields = (
  startIso: string,
  endIso: string,
  timeZone?: string
): Partial<Pick<EventsHubCard, 'startAt' | 'endAt' | 'timeLabel' | 'dateChip' | 'timeZone'>> => {
  const timeLabel = formatEventWeekdayTimeLabel(startIso, timeZone)
  const dateChip = formatEventDateChip(startIso, endIso, timeZone)
  return {
    ...(startIso ? { startAt: startIso } : {}),
    ...(endIso ? { endAt: endIso } : {}),
    ...(timeLabel ? { timeLabel } : {}),
    ...(dateChip ? { dateChip } : {}),
    ...(timeZone ? { timeZone } : {}),
  }
}

export const getEventDurationParts = (startIso: string, endIso: string, timeZone?: string): EventDurationParts | undefined => {
  const start = new Date(startIso)
  const end = new Date(endIso)
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end.getTime() <= start.getTime()) return undefined

  const days = inclusiveCalendarDays(start, end, timeZone)
  if (days !== undefined && days >= 2) return { unit: 'day', count: days }

  const minutes = Math.round((end.getTime() - start.getTime()) / 60000)
  if (minutes >= 120) return { unit: 'hour', count: Math.round(minutes / 60) }
  return { unit: 'minute', count: Math.max(1, minutes) }
}

export const getLiveProgressParts = (startIso: string, endIso: string, now: Date, timeZone?: string): LiveProgressParts | undefined => {
  const start = new Date(startIso)
  const end = new Date(endIso)
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return undefined

  const startDay = toUtcCalendarDay(start, timeZone)
  const endDay = toUtcCalendarDay(end, timeZone)
  const nowDay = toUtcCalendarDay(now, timeZone)
  if (startDay === undefined || endDay === undefined || nowDay === undefined) return undefined

  const total = Math.round((endDay - startDay) / MS_PER_DAY) + 1
  if (total >= 2) {
    const current = Math.min(total, Math.max(1, Math.round((nowDay - startDay) / MS_PER_DAY) + 1))
    return { type: 'day', current, total }
  }

  const minutes = Math.max(0, Math.round((end.getTime() - now.getTime()) / 60000))
  const hours = Math.floor(minutes / 60)
  return { type: 'remaining', hours, minutes: hours > 0 ? minutes % 60 : minutes }
}

const toFocalPoint = (value: unknown): EventsHubImageFocalPoint | undefined => {
  const point = asRecord(value)
  if (!point) return undefined
  const x = asNumber(point.x)
  const y = asNumber(point.y)
  if (x === undefined || y === undefined) return undefined
  return { x, y }
}

export const isEventLiveNow = (startIso: string, endIso: string, now: Date): boolean => {
  const start = new Date(startIso)
  const end = new Date(endIso)
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return false
  return now.getTime() >= start.getTime() && now.getTime() <= end.getTime()
}

export const mapEventsManagementEvent = (value: unknown, now: Date = new Date()): EventsHubCard | undefined => {
  const event = asRecord(value)
  if (!event) return undefined

  const id = asString(event.id)
  const title = asString(event.name)
  if (!id || !title) return undefined

  const startDateTime = asString(event.startDateTime)
  const endDateTime = asString(event.endDateTime)
  const timezone = asString(event.timezone) || undefined
  const live = isEventLiveNow(startDateTime, endDateTime, now)
  const imageFocalPoint = toFocalPoint(event.coverImageFocalPoint)
  const description = asString(event.summary)
  const displayTimeZone = resolveEventDisplayTimeZone(asRecord(event.location), timezone)

  return {
    id,
    title,
    imageUrl: asString(event.coverImage),
    dateLabel: formatEventDateLabel(startDateTime, endDateTime, displayTimeZone),
    locationLabel: toEventLocationLabel(asRecord(event.location)),
    ...toEventListingFields(startDateTime, endDateTime, displayTimeZone),
    ...(description ? { description } : {}),
    ...(imageFocalPoint ? { imageFocalPoint } : {}),
    ...(live ? { status: 'live', isLive: true } : {}),
  }
}

export const mapEventsManagementEvents = (values: readonly unknown[], now: Date = new Date()): EventsHubCard[] =>
  values.flatMap(value => {
    const mapped = mapEventsManagementEvent(value, now)
    return mapped ? [mapped] : []
  })
